import { ElMessage } from "element-plus";
import { ZhConfirm } from "zhihao-ui";

type Decorator = MethodDecorator;

type ConfirmableOptions = {
    /** 状态在参数中的索引, 默认参数最后一项 */
    switchParamIndex?: number;
    /** 消息文本 */
    msgs: [string, string];
    /** 确认后的消息提示文本 */
    completedMsgs?: [string, string];
    /** 当参数为对象时, 状态参数的字段名 */
    switchParamFiled?: string;
};
type ConfirmableDecorator = Decorator & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends (...args: any[]) => any>(fn: T): T;
};
function confirmable(options: ConfirmableOptions): ConfirmableDecorator;
function confirmable(msg: string, title?: string, completedMsg?: string, confirmButtonText?: string): ConfirmableDecorator;
function confirmable(
    msg: string | ConfirmableOptions,
    title?: string,
    completedMsg?: string,
    confirmButtonText?: string
): ConfirmableDecorator {
    const decorateDescriptor = (descriptor: PropertyDescriptor) => {
        /**
         * 包裹confirm的函数
         * @param callBack 在确认后需要调用的函数
         * @param msg 确认文本
         * @param completedMsg 确认后的消息提示文本
         * @returns callBack返回值
         */
        const confirmWrapper = async <T>(
            callBack: () => T | Promise<T>,
            msg: string,
            completedMsg?: string
        ): Promise<T | undefined> => {
            // 为每个弹框实例生成唯一ID
            const uniqueId = `zh-confirm-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
            const res = await ZhConfirm(msg, title ?? "提示", {
                closeOnClickModal: false,
                showClose: true,
                confirmButtonText: confirmButtonText ?? "确认",
                confirmButtonClass: `zh-confirm-confirm-button`,
                cancelButtonClass: `zh-confirm-cancel-button ${uniqueId}-cancel`,
                beforeClose: async (action: unknown, instance: unknown, done: () => void) => {
                    beforeCloseZhConfirm(action, instance, done, uniqueId);
                }
            });

            if (res) {
                const result = await callBack();

                // 执行成功后的消息提示
                /**如果completedMsg不存在, 则截取'是否'后的两个字 */
                const message = completedMsg ?? /是否(.{2})/.exec(msg)?.at(1)?.replace(/^/, "已");
                if (message) {
                    ElMessage.success(message);
                }

                return result;
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const originMethod = descriptor.value as (...args: any[]) => Promise<any>;
        if (typeof msg === "string") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            descriptor.value = async function (...args: any[]) {
                return confirmWrapper(async () => originMethod.apply(this, args), msg, completedMsg);
            };
        } else if (typeof msg === "object") {
            const options = msg;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            descriptor.value = async function (...args: any[]) {
                const switchParamIndex = options.switchParamIndex ?? args.length - 1;
                const switchParamValue = options.switchParamFiled
                    ? args[switchParamIndex][options.switchParamFiled]
                    : args[switchParamIndex];
                return confirmWrapper(
                    async () => originMethod.apply(this, args),
                    options.msgs[switchParamValue],
                    options.completedMsgs?.at(switchParamValue)
                );
            };
        }
    };

    const decorator: Decorator = (_target, _propertyKey, descriptor) => {
        decorateDescriptor(descriptor);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((target: any, propertyKey?: string, descriptor?: PropertyDescriptor) => {
        if (typeof propertyKey === "undefined" && typeof descriptor === "undefined" && typeof target === "function") {
            const fnDescriptor: PropertyDescriptor = {
                configurable: true,
                enumerable: false,
                writable: true,
                value: target
            };
            decorateDescriptor(fnDescriptor);
            return fnDescriptor.value;
        }
        return decorator(target, propertyKey as string, descriptor as PropertyDescriptor);
    }) as ConfirmableDecorator;
}
export { confirmable };

// 点击【确认】按钮时显示 loading 并禁用确认按钮，禁用取消按钮
export const beforeCloseZhConfirm = (action: unknown, instance: unknown, done: () => void, cancelButtonClass: string) => {
    const messageBoxState = instance as { confirmButtonLoading: boolean; confirmButtonDisabled: boolean };
    if (action === "confirm") {
        // 点击【确认】按钮时显示 loading 并禁用确认按钮
        messageBoxState.confirmButtonLoading = true;
        messageBoxState.confirmButtonDisabled = true;

        // 禁用取消按钮
        const cancelButton = document.querySelector(`.zh-message-confirm .${cancelButtonClass}-cancel`) as HTMLButtonElement;
        if (cancelButton) {
            cancelButton.disabled = true;
            cancelButton.classList.add("is-disabled");
        }

        // 关闭弹框
        done();
    } else {
        // 取消或关闭时直接关闭弹框，不执行任何操作
        done();
    }
};
